import { useState, useCallback, useMemo } from 'react';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qDiagnosesByEncounter,
  qDiagnosisSuggestions,
  qDiagnosisSuggestionsByEncounter,
  type PatientDiagnosisEntryRow,
  type PatientDiagnosisSuggestionRow,
  type VttSuggestionRow,
} from '@helios/data-access';
import { ChipListField, type Chip } from './ChipListField';

/* ------------------------------------------------------------------ */
/*  Diagnosis structured entry (PRD.214 Phase 2 + PRD.217).           */
/*                                                                    */
/*  AI dermatology suggestions are auto-accepted on the client when   */
/*  analysis completes — they land as regular diagnosis chips with    */
/*  an inline "AI Suggested" badge so clinicians can dismiss them     */
/*  with one click (the chip X) if they don't agree.                  */
/* ------------------------------------------------------------------ */

interface Props {
  patientId: string;
  encounterId: string;
  /** When true, hide the internal "Diagnosis" header (used inside guided-note cards). */
  hideHeader?: boolean;
}

export function EncounterDiagnosesTab({ patientId, encounterId, hideHeader }: Props) {
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const { data: entries = [] } = usePowerSyncQuery<PatientDiagnosisEntryRow>(
    qDiagnosesByEncounter(encounterId),
  );

  // PRD.217 — pull suggestions so we can tag each linked entry with an
  // "AI Suggested" badge. Auto-accept means we only care about rows whose
  // status === 'accepted' and have a populated accepted_diagnosis_entry_id.
  const { data: suggestionRowsForBadge = [] } = usePowerSyncQuery<PatientDiagnosisSuggestionRow>(
    qDiagnosisSuggestionsByEncounter(encounterId),
  );

  const aiEntryIds = useMemo(() => {
    const set = new Set<string>();
    for (const s of suggestionRowsForBadge) {
      if (s.status === 'accepted' && s.accepted_diagnosis_entry_id) {
        set.add(s.accepted_diagnosis_entry_id);
      }
    }
    return set;
  }, [suggestionRowsForBadge]);

  const [search, setSearch] = useState('');
  const { data: suggestionRows = [] } = usePowerSyncQuery<VttSuggestionRow>(
    qDiagnosisSuggestions(orgId, search),
  );
  const suggestions = useMemo(() => suggestionRows.map((r) => r.label), [suggestionRows]);

  const chips: Chip[] = useMemo(
    () =>
      entries.map((e) => ({
        id: e.id,
        label: e.label,
        badge: aiEntryIds.has(e.id) ? 'AI Suggested' : undefined,
      })),
    [entries, aiEntryIds],
  );

  const handleAdd = useCallback(
    async (label: string) => {
      if (!db) return;
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO patient_diagnosis_entries
          (id, org_id, clinic_id, patient_id, encounter_id, label, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [crypto.randomUUID(), orgId, null, patientId, encounterId, label, now, now],
      );
    },
    [db, orgId, patientId, encounterId],
  );

  const handleRemove = useCallback(
    async (chip: Chip) => {
      if (!db) return;
      await db.execute(`DELETE FROM patient_diagnosis_entries WHERE id = ?`, [chip.id]);
    },
    [db],
  );

  return (
    <div
      style={{
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      {!hideHeader && (
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Diagnosis
          </h2>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            Working diagnosis for this encounter.
          </p>
        </div>
      )}

      <ChipListField
        chips={chips}
        suggestions={suggestions}
        onSearchChange={setSearch}
        onAdd={handleAdd}
        onRemove={handleRemove}
        subtitle="Add or edit the diagnosis"
        addLabel="Add Diagnosis"
        placeholder="Add or edit diagnosis…"
      />
    </div>
  );
}
